import { Component, Inject, Input, EventEmitter, Output } from '@angular/core';

import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';

import { MultiExtendedFilterableSelect2ReinitData } from 'src/main/angular/app/shared/filterable-select/components/multi-extended-filterable-select2.component';

import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { MemoCategory } from '../../../../static-types/entities/memo-category.entity';
import { MemoSubCategory } from '../../../../static-types/entities/memo-sub-category.entity';

import { ParcTLR } from '../../parc/entities/parc-tlr.entity';

import { SousParcService } from '../../sous-parc/services/sous-parc.service';
import { SousParcTLR } from '../../sous-parc/entities/sous-parc-tlr.entity';

import { OuvrageService } from '../services/ouvrage.service';
import { OuvrageTLR } from '../entities/ouvrage-tlr.entity';

import { MemoService } from '../../../memo/services/memo.service';
import { Memo } from '../../../memo/entities/memo.entity';
import { SubjectRelay } from 'src/main/angular/app/shared/commands/subject-relay.class';
import { getId } from 'src/main/angular/app/shared/basic-data-management/has-id.interface';

export type OuvrageBySousParcMultiSelectorWithNotesReinitData = {
	parc: ParcTLR,
	ouvrageFilter?: (parc: ParcTLR, ouvrage: OuvrageTLR) => boolean,
	ouvrageSelector?: (ouvrage: OuvrageTLR) => boolean,
	selectedOuvrageIds?: number[],
};

var allowLog = false;

var _logTap = <T> (label : string) => tap(
	(data: T) => {
		if (allowLog)
			console.log("obspmswn2 - " + label + ": ", data);
	}
);

@Component({
	selector: 'ouvrage-by-sous-parc-multi-selector-with-notes',
	templateUrl: 'ouvrage-by-sous-parc-multi-selector-with-notes.html',
	styleUrls: ['ouvrage-by-sous-parc-multi-selector.css']
})
export class OuvrageBySousParcMultiSelectorWithNotesComponent {
	memoCategoriesById!: Map <number, MemoCategory>;
	memoSubCategoriesById!:  Map <number, MemoSubCategory>;
	idClasseOnd!: number;
	
	sousParcs: SousParcTLR[] = [];
	memosByOuvrageId: Map<number, Memo[]> = new Map<number, Memo[]>();

	getSousParcLabel = (sousParc: SousParcTLR) => '...dans ' + sousParc.description + ' (' + sousParc.code + ')';
	getOuvrageMemos = (ouvrage: OuvrageTLR) => {
		let memos = this.memosByOuvrageId.get(ouvrage.id);
		if (!memos) {
			return [] as Memo[];
		}
		return memos.sort((a, b) => a.niveauId > b.niveauId ? 1 : a.niveauId === b.niveauId ? 0 : -1);;
	}
	getMemoCategorie = (memo: Memo) => {
		if (memo.categorieId && this.memoCategoriesById.has(memo.categorieId)) {
			let category = this.memoCategoriesById.get(memo.categorieId);
			if (category) {
			let label = category.label;
				if (memo.sousCategorieId && this.memoSubCategoriesById.has(memo.sousCategorieId)) {
					let subCategory = this.memoSubCategoriesById.get(memo.sousCategorieId);
					if (subCategory) {
						label = label + ' (' + subCategory.label + ') '
					}
				}
				return  label + ' - ';
			}
		}
		return '';
	}
	
	checkCategory = (ouvrage: OuvrageTLR, sousParc: SousParcTLR) => ouvrage.sousParcId === sousParc.id;
	
	reinitSubject = new BehaviorSubject<MultiExtendedFilterableSelect2ReinitData | null>(null);

	constructor( //
		@Inject(StaticTypeService) staticTypeService: StaticTypeService, //
		@Inject(JSR310ConversionService) private jSR310ConversionService: JSR310ConversionService, //
		@Inject(SousParcService) private sousParcService: SousParcService, //
		@Inject(OuvrageService) private ouvrageService: OuvrageService, //
		@Inject(MemoService) private memoService: MemoService //
	) {
		/////////////////////////////////
		// Calcul des valeurs derivees //
		/////////////////////////////////
		
		/////////////////////////////////////////
		// Binding des informations sur la vue //
		/////////////////////////////////////////
	
		//////////////////////////////////////
		// Chargement des donnees statiques //
		//////////////////////////////////////

		staticTypeService.getMemoCategoryMapById().subscribe(
			(map: Map <number, MemoCategory>) => this.memoCategoriesById = map
		);
		staticTypeService.getMemoSubCategoryMapById().subscribe(
			(map: Map <number, MemoSubCategory>) => this.memoSubCategoriesById = map
		);
		
		ouvrageService.getClasseOuvrageList().subscribe(
			(classes) => {
				let idClasseOnd = classes.filter($ => $.code === 'OND')[0].id;
				this.idClasseOnd = idClasseOnd;
			}
		)
		
		//////////////////////////////////////////////////
		// Traitement des demandes de reinitialisations //
		//////////////////////////////////////////////////
		
		let reinitData$ = this.reinitCommand$.pipe(
			filter(
				(data) => data != null
			),
			map(
				(data) => data as OuvrageBySousParcMultiSelectorWithNotesReinitData
			),
			mergeMap(
				({
					parc,
					ouvrageFilter,
					ouvrageSelector,
					selectedOuvrageIds,
				}) => {
					let sousParc$ = this.sousParcService.getSousParcTLRByParc(parc.id);
					let ouvrageOfParc$ = this.ouvrageService.getOuvrageTLRByParc(parc.id);
					let filteredSortedOuvrages$ = ouvrageOfParc$.pipe(
						map(
							(ouvrages) => {
								if (ouvrages.length == 0) {
									return [];
								}
								
								if (ouvrageFilter === undefined) {
									return ouvrages;
								}
								let nnOuvrageFilter = ouvrageFilter;
								
								let filteredOuvrages = ouvrages
									.filter($ => nnOuvrageFilter(parc, $))
									.sort((o1, o2) => o1.nom.localeCompare(o2.nom))
								;
								
								return filteredOuvrages;
							}
						),
						map(
							// Ticket 54948: on veut que les ouvrages soient classes par leur nom
							// NB: pas defaut, le serveur les renvoie tries par leur description
							(ouvrages) => ouvrages.sort(
								(o1, o2) => o1.nom.localeCompare(o2.nom)
							)
						),
					);
					let isOuvragePreselected = undefined;
					if (ouvrageSelector != undefined) {
						isOuvragePreselected = ouvrageSelector;
					} else if (selectedOuvrageIds != undefined) {
						let si = selectedOuvrageIds;
						isOuvragePreselected = (ouvrage: OuvrageTLR) => si.includes(ouvrage.id);
					}
					
					return forkJoin({
						sousParcs: sousParc$,
						filteredSortedOuvrages: filteredSortedOuvrages$,
						isOuvragePreselected: of(isOuvragePreselected),
					});
				}
			),
			_logTap("reinitData$"),
		);
				
		//////////////////////////////// 
		// Propagation des evenements //
		////////////////////////////////
		
		reinitData$.subscribe(
			(data) => {
				this.sousParcs = data.sousParcs;
							
				// Attention, ici, le sous composant peut ne pas avoir encore recu la derniere liste de valeurs
				// donc il faut lui passer de force
				this.reinitSubject.next({
					list: data.filteredSortedOuvrages,
					preselectionFunction: data.isOuvragePreselected,
				});
			}		
		);
	}

	@Input() id!: string;
	@Input() disabled = false;
	@Input() maxNumberOfSelections: number | null = null;
	
	// Classes pour l'affichege
	public _labelClass: string = 'col-md-1';
	@Input() set labelClass(value: string | undefined) {
		if (value) {
			this._labelClass = value;
		}	
	}
	
	public _fieldClass: string = 'col-md-7';
	@Input() set fieldClass(value: string | undefined) {
		if (value) {
			this._fieldClass = value;
		}
	}
	
	public _notesClass: string = 'col-md-5';
	@Input() set notesClass(value: string | undefined) {
		if (value) {
			this._notesClass = value;
		}
	}
	
	public getOuvrageId = getId;

	public getOuvrageLabel = (ouvrage: OuvrageTLR | null | undefined) => {
		if (ouvrage) {
			return ouvrage.nom + (ouvrage.numeroSerie ? ' (' + ouvrage.numeroSerie + ')': '');
		}
		return '';
	};
	@Input() set ouvrageLabelGetter(labelGetterFunction: (ouvrage: OuvrageTLR) => string) {
		this.getOuvrageLabel = (ouvrage: OuvrageTLR | null | undefined) => {
	  		if (ouvrage) {
 				return labelGetterFunction(ouvrage);
			}
			return '';
		};
	} 
	
	private localDateRef: string | null = null; 
	@Input() set dateRef(value: Date | null) {
		this.localDateRef = value ? this.jSR310ConversionService.toLocalDateTimeParam(value) : null;
	}
	
	private reinitCommand$ = new SubjectRelay<OuvrageBySousParcMultiSelectorWithNotesReinitData | null>();
	@Input() set reinitCommands(obj: Observable<OuvrageBySousParcMultiSelectorWithNotesReinitData | null>){
		this.reinitCommand$.registerSource(obj);
	}
	
	@Output() selectedOuvrageIdsChange = new EventEmitter<number[]>();
	
	@Output() selectedOuvragesChange = new EventEmitter<OuvrageTLR[]>();
	
	onSelectedOuvragesChange(ouvrages: OuvrageTLR[]) {
		let ids = ouvrages.map((ouvrage: OuvrageTLR) => ouvrage.id);
		
		this.selectedOuvrageIdsChange.emit(ids);
		this.selectedOuvragesChange.emit(ouvrages);

		if (ids.length === 0) {
			this.memosByOuvrageId = new Map<number, Memo[]>();
		} else {
			if (this.localDateRef === null) {
				throw Error("dateRef n'est pas initialisee");
			}

			let idsParam = ids.join(',');
			this.memoService.getMemosOnOuvragesForDate(idsParam, this.localDateRef).subscribe(
				(memos: Memo[]) => {
					this.memosByOuvrageId = new Map<number, Memo[]>();
					for (let memo of memos) {
						let ouvrageIds = memo.ouvrageIds;
						for (let ouvrageId of ouvrageIds) {
							if (ids.indexOf(ouvrageId) > -1) {
								let memosForOuvrage = this.memosByOuvrageId.get(ouvrageId);
								if (!memosForOuvrage) {
									memosForOuvrage = [] as Memo[];
									this.memosByOuvrageId.set(ouvrageId, memosForOuvrage);
								}
								memosForOuvrage.push(memo);
							}
						}
					}
				}
			);
		}
	}
}